import type * as React from 'react';

import * as SheetPrimitive from '@radix-ui/react-dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowLeftIcon, XIcon } from 'lucide-react';

import { cn } from '../lib/utils';
import { Button } from './button';

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
    return <SheetPrimitive.Root data-slot="sheet" {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
    return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
    return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
    return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
}

function SheetCloseButton({
    className,
    size,
    label = 'Close',
    ...props
}: React.ComponentProps<typeof Button> & { label?: string }) {
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={cn('opacity-70 transition-opacity hover:opacity-100', className)}
            {...props}
            data-slot="sheet-close-button"
        >
            <XIcon className="size-4" />
            <span className="sr-only">{label}</span>
        </Button>
    );
}

function SheetOverlay({
    className,
    ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
    return (
        <SheetPrimitive.Overlay
            data-slot="sheet-overlay"
            className={cn(
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
                className,
            )}
            {...props}
        />
    );
}

const SHEET_GUTTER = 'px-[var(--sheet-gutter)]';

const sheetContentVariants = cva(
    'bg-card data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500',
    {
        variants: {
            side: {
                right: 'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full border-l',
                left: 'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full border-r',
                top: 'data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 border-b',
                bottom: 'data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 border-t',
            },
            size: {
                small: '',
                medium: '',
                large: '',
                full: '',
            },
            variant: {
                default: '[--sheet-gutter:--spacing(6)]',
                chart: '[--sheet-gutter:--spacing(4)]',
            },
        },
        compoundVariants: [
            { side: ['right', 'left'], size: 'small', class: 'w-full sm:w-90' },
            { side: ['right', 'left'], size: 'medium', class: 'w-full md:w-144 lg:w-180' },
            {
                side: ['right', 'left'],
                size: 'large',
                class: 'w-full md:w-[90%] lg:w-[80%] xl:max-w-288',
            },
            { side: ['top', 'bottom'], size: 'small', class: 'h-auto' },
            { side: ['top', 'bottom'], size: 'medium', class: 'h-auto min-h-90' },
            { side: ['top', 'bottom'], size: 'large', class: 'h-auto min-h-[75vh]' },
            { side: 'bottom', size: ['small', 'medium', 'large'], class: 'max-h-[90dvh]' },
            { side: ['right', 'left'], size: 'full', class: 'inset-0 w-full border-none' },
            { side: ['top', 'bottom'], size: 'full', class: 'inset-0 h-full border-none' },
        ],
        defaultVariants: {
            side: 'right',
            size: 'small',
            variant: 'default',
        },
    },
);

function SheetContent({
    className,
    children,
    side = 'right',
    size = 'small',
    variant = 'default',
    showCloseButton = true,
    ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> &
    VariantProps<typeof sheetContentVariants> & {
        showCloseButton?: boolean;
    }) {
    return (
        <SheetPortal>
            <SheetOverlay />
            <SheetPrimitive.Content
                data-slot="sheet-content"
                className={cn(sheetContentVariants({ side, size, variant }), className)}
                {...props}
            >
                {showCloseButton && (
                    <SheetClose asChild>
                        <SheetCloseButton className="absolute top-1 right-1 ring-offset-card" />
                    </SheetClose>
                )}
                {children}
            </SheetPrimitive.Content>
        </SheetPortal>
    );
}

function SheetBody({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div data-slot="sheet-body" className={cn('py-6', SHEET_GUTTER, className)} {...props} />
    );
}

const sheetHeaderVariants = cva('', {
    variants: {
        variant: {
            default: `flex flex-col gap-1 ${SHEET_GUTTER} pt-10 pb-3`,
            mini: 'flex items-center  gap-4 px-4 py-4.5 [&_[data-slot=sheet-title]]:flex-1 [&_[data-slot=sheet-title]]:text-base',
            breadcrumbs: `flex items-center justify-between py-3 ${SHEET_GUTTER} [&_[data-slot=sheet-close-button]]:size-8`,
            chart: `flex flex-col gap-4 ${SHEET_GUTTER} pt-4 pb-6 [&_[data-slot=sheet-title]]:font-bold`,
        },
        bordered: {
            true: 'border-b',
            false: '',
        },
    },
    defaultVariants: {
        variant: 'default',
        bordered: false,
    },
});

function SheetHeader({
    className,
    variant = 'default',
    bordered = false,
    ...props
}: React.ComponentProps<'div'> & VariantProps<typeof sheetHeaderVariants>) {
    return (
        <div
            data-slot="sheet-header"
            className={cn(sheetHeaderVariants({ variant, bordered }), className)}
            {...props}
        />
    );
}

function SheetBackButton({ className, ...props }: React.ComponentProps<typeof Button>) {
    return (
        <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            data-slot="sheet-back-button"
            className={cn('shrink-0', className)}
            {...props}
        >
            <ArrowLeftIcon className="size-4" />
            <span className="sr-only">Back</span>
        </Button>
    );
}

const sheetFooterVariants = cva('mt-auto', {
    variants: {
        orientation: {
            horizontal: `border-muted flex min-h-16 items-center justify-end gap-2 border-t px-6 py-3`,
            stacked: `flex flex-col gap-2 border-muted border-t px-6 py-3`,
        },
    },
    defaultVariants: {
        orientation: 'horizontal',
    },
});

function SheetFooter({
    className,
    orientation = 'horizontal',
    ...props
}: React.ComponentProps<'div'> & VariantProps<typeof sheetFooterVariants>) {
    return (
        <div
            data-slot="sheet-footer"
            className={cn(sheetFooterVariants({ orientation }), className)}
            {...props}
        />
    );
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
    return (
        <SheetPrimitive.Title
            data-slot="sheet-title"
            className={cn('text-foreground text-2xl font-medium', className)}
            {...props}
        />
    );
}

function SheetDescription({
    className,
    ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
    return (
        <SheetPrimitive.Description
            data-slot="sheet-description"
            className={cn('text-muted-foreground text-base', className)}
            {...props}
        />
    );
}

export {
    Sheet,
    SheetBackButton,
    SheetBody,
    SheetClose,
    SheetCloseButton,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
};
